import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { BrandName } from './BrandName';
export function Testimonials() {
  const allTestimonials = [{
    quote: 'Incredible service! Our sales increased by 40% after the new site launch.',
    role: 'Small Business Owner',
    initials: 'S.B.'
  }, {
    quote: 'Very professional and easy to work with. Our social media engagement tripled!',
    role: 'Marketing Director',
    initials: 'M.D.'
  }, {
    quote: 'The SEO improvements brought us to the first page on Google.',
    role: 'E-commerce Founder',
    initials: 'E.F.'
  }, {
    quote: 'Highly recommend! They really understand what small businesses need.',
    role: 'Restaurant Owner',
    initials: 'R.O.'
  }, {
    quote: 'Our website now looks amazing and runs flawlessly on mobile.',
    role: 'Freelance Artist',
    initials: 'F.A.'
  }, {
    quote: 'They took the time to understand our brand voice. Could not be happier.',
    role: 'Boutique Owner',
    initials: 'B.O.'
  }, {
    quote: 'Our leads grew by 60% thanks to their social media strategies.',
    role: 'Real Estate Agent',
    initials: 'R.A.'
  }, {
    quote: 'Fast, reliable, and truly dedicated to results.',
    role: 'Tech Startup Founder',
    initials: 'T.F.'
  }, {
    quote: 'We saw a huge jump in customer engagement after the redesign.',
    role: 'Fitness Coach',
    initials: 'F.C.'
  }, {
    quote: 'Super easy to work with, and the results speak for themselves.',
    role: 'Event Planner',
    initials: 'E.P.'
  }, {
    quote: 'Our conversions went up 35% after the SEO overhaul.',
    role: 'Online Store Owner',
    initials: 'O.O.'
  }, {
    quote: 'They made everything easy to understand, even for a non-tech person like me.',
    role: 'Café Owner',
    initials: 'C.O.'
  }, {
    quote: 'We finally have a website we are proud of. Thank you!',
    role: 'Nonprofit Director',
    initials: 'N.D.'
  }, {
    quote: 'Their social media management brought us a flood of new followers.',
    role: 'Fashion Brand Owner',
    initials: 'F.B.'
  }, {
    quote: 'They delivered on time and exceeded our expectations.',
    role: 'Consultant',
    initials: 'C.N.'
  }];
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 3;
  const totalPages = Math.ceil(allTestimonials.length / itemsPerPage);
  const nextPage = () => {
    setCurrentPage(prev => (prev + 1) % totalPages);
  };
  const prevPage = () => {
    setCurrentPage(prev => (prev - 1 + totalPages) % totalPages);
  };
  const currentTestimonials = allTestimonials.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);
  // Helper to generate a random background color for avatars based on initials
  const getAvatarColor = (index: number) => {
    const colors = ['bg-sum-green', 'bg-sum-gold', 'bg-blue-600', 'bg-purple-600', 'bg-indigo-600', 'bg-pink-600'];
    return colors[index % colors.length];
  };
  return <section id="testimonials" className="py-20 bg-sum-light overflow-hidden" data-id="element-203">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-204">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-205">
          <h2 className="text-3xl md:text-4xl font-extrabold text-sum-charcoal mb-4" data-id="element-206">
            Don't Just Take My Word For It
          </h2>
          <p className="text-xl text-gray-600" data-id="element-207">
            See what other business owners are saying about working with{' '}
            <BrandName data-id="element-208" /> Marketing.
          </p>
        </div>

        <div className="relative" data-id="element-209">
          <motion.div key={currentPage} initial={{
          opacity: 0,
          x: 50
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -50
        }} transition={{
          duration: 0.4
        }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-210">
            {currentTestimonials.map((testimonial, index) => <div key={index} className="bg-sum-light rounded-2xl p-8 shadow-sm border border-gray-100 relative" data-id="element-211">
                <Quote className="absolute top-6 right-6 text-gray-200" size={40} data-id="element-212" />
                <div className="flex space-x-1 mb-6" data-id="element-213">
                  {[...Array(5)].map((_, i) => <Star key={i} className="text-sum-gold fill-current" size={20} data-id="element-214" />)}
                </div>
                <p className="text-gray-700 text-lg italic mb-8 relative z-10 min-h-[100px]" data-id="element-215">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center" data-id="element-216">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${getAvatarColor(currentPage * itemsPerPage + index)}`} data-id="element-217">
                    {testimonial.initials}
                  </div>
                  <div className="ml-4" data-id="element-218">
                    <p className="text-sm font-bold text-sum-charcoal" data-id="element-219">
                      Verified Client
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-220">{testimonial.role}</p>
                  </div>
                </div>
              </div>)}
          </motion.div>

          {/* Navigation Controls */}
          <div className="flex justify-center items-center mt-12 space-x-6" data-id="element-221">
            <button onClick={prevPage} className="p-3 rounded-full bg-white border border-gray-200 text-gray-600 hover:text-sum-green hover:border-sum-green hover:shadow-md transition-all" aria-label="Previous testimonials" data-id="element-222">
              <ChevronLeft size={24} data-id="element-223" />
            </button>
            <div className="flex space-x-2" data-id="element-224">
              {[...Array(totalPages)].map((_, i) => <button key={i} onClick={() => setCurrentPage(i)} className={`w-3 h-3 rounded-full transition-colors ${currentPage === i ? 'bg-sum-green' : 'bg-gray-300'}`} aria-label={`Go to page ${i + 1}`} data-id="element-225" />)}
            </div>
            <button onClick={nextPage} className="p-3 rounded-full bg-white border border-gray-200 text-gray-600 hover:text-sum-green hover:border-sum-green hover:shadow-md transition-all" aria-label="Next testimonials" data-id="element-226">
              <ChevronRight size={24} data-id="element-227" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}